/**
 * Cross-browser share helper for render images.
 *
 * Tries (in order):
 *   1. Web Share API with `files` — mobile Chrome / Safari iOS expose the
 *      native sheet with the image preview + Save Image / AirDrop options.
 *   2. Web Share API with `url` only — desktop Safari + Edge.
 *   3. Clipboard fallback — desktop browsers without `navigator.share`.
 *
 * Mirrors the iOS/Android Share button which always ships an actual image
 * (not a bare URL) so the Sharesheet preview shows the rendering itself.
 */

interface ShareRenderImageOptions {
  url: string;
  filename: string;
  title?: string;
  onClipboardFallback?: () => void;
}

export async function shareRenderImage({
  url,
  filename,
  title,
  onClipboardFallback,
}: ShareRenderImageOptions): Promise<void> {
  if (await tryShareAsFile({ url, filename, title })) return;
  if (await tryShareAsUrl({ url, title })) return;
  await copyToClipboardFallback(url, onClipboardFallback);
}

async function tryShareAsFile({
  url,
  filename,
  title,
}: {
  url: string;
  filename: string;
  title?: string;
}): Promise<boolean> {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
    return false;
  }
  if (typeof navigator.canShare !== 'function') return false;

  try {
    const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!response.ok) return false;
    const blob = await response.blob();
    const file = new File([blob], filename, {
      type: blob.type || 'image/jpeg',
    });
    if (!navigator.canShare({ files: [file] })) return false;
    await navigator.share({ files: [file], title });
    return true;
  } catch (error) {
    // User-cancelled share counts as handled — don't fall through to URL share.
    if ((error as { name?: string })?.name === 'AbortError') return true;
    return false;
  }
}

async function tryShareAsUrl({
  url,
  title,
}: {
  url: string;
  title?: string;
}): Promise<boolean> {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
    return false;
  }
  try {
    await navigator.share({ url, title });
    return true;
  } catch (error) {
    if ((error as { name?: string })?.name === 'AbortError') return true;
    return false;
  }
}

async function copyToClipboardFallback(
  url: string,
  onClipboardFallback?: () => void
): Promise<void> {
  try {
    await navigator.clipboard.writeText(url);
  } finally {
    onClipboardFallback?.();
  }
}
